import {doesKeyExist, LocaleSafeString, markLocaleSafe, TranslationKey} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';

// MEAD-42 error body. Mirrors `StructuredErrorPayload` in api/version_2_0/__init__.py.
export interface StructuredApiErrorPayload {
  error_code: string;
  detail?: string;
  params?: Record<string, unknown>;
}

const REQUIRED_STRING_KEYS: ReadonlyArray<keyof StructuredApiErrorPayload> = [`error_code`];

// Skips `status`: webapp's APIErrorResponse always emits `"status": "error"` (the iron
// `handleErrors` sentinel), so it isn't a useful discriminator. HTTP status lives on Response.
export function isStructuredApiErrorPayload(value: unknown): value is StructuredApiErrorPayload {
  if (typeof value !== `object` || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  for (const key of REQUIRED_STRING_KEYS) {
    if (typeof record[key] !== `string`) {
      return false;
    }
  }
  if (record.detail !== undefined && typeof record.detail !== `string`) {
    return false;
  }
  return record.params === undefined || (typeof record.params === `object` && record.params !== null);
}

export function parseStructuredApiErrorPayload(body: unknown): StructuredApiErrorPayload | null {
  return isStructuredApiErrorPayload(body) ? extractStructuredPayload(body) : null;
}

function extractStructuredPayload(body: StructuredApiErrorPayload): StructuredApiErrorPayload {
  // Copy only the structured fields (dropping undefined optionals) so the
  // payload never carries the request/error/sentinel keys from the response body.
  return {
    error_code: body.error_code,
    ...(body.detail !== undefined && {detail: body.detail}),
    ...(body.params !== undefined && {params: body.params}),
  };
}

export interface StructuredApiError extends Error {
  errorCode?: string;
  structuredPayload?: StructuredApiErrorPayload;
}

export function attachStructuredPayload(err: Error, payload: StructuredApiErrorPayload | null): StructuredApiError {
  const augmented = err as StructuredApiError;
  if (payload) {
    augmented.structuredPayload = payload;
    augmented.errorCode = payload.error_code;
  }
  return augmented;
}

// Falls back to the server-supplied English detail when no translation is
// registered. Returns null when neither is available; callers should fall back
// to the exception message (the server-supplied user-visible string).
export function getLocalizedApiErrorDetail(
  error: StructuredApiError,
  i18n: LocalizationStore,
): LocaleSafeString | null {
  const payload = error.structuredPayload;
  if (!payload) {
    return null;
  }
  const key = `api-errors.${payload.error_code}.detail`;
  if (doesKeyExist(key)) {
    return i18n.t(key as TranslationKey, payload.params);
  }
  return payload.detail !== undefined ? markLocaleSafe(payload.detail) : null;
}
